import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import ODataStore from 'devextreme/data/odata/store';
import { ApiErrorHandlerService } from '@bds/helpers';
import { RtShopFact, RtShopFactAdapter } from '@bds/railtrac-models';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class RtShopFactService {
    constructor(
        private http: HttpClient,
        public adapter: RtShopFactAdapter,
        @Inject('BASE_API_URL') private apibaseUrl: string,
        @Inject('BASE_ODATA_URL') private odatabaseUrl: string,
        private apiErrorHandler: ApiErrorHandlerService,
    ) {}

    controllerName = 'ShopFact';
    secondControllerName = 'RtShopFact';

    getODataStore(): ODataStore {
        const keyField = 'shopNo';

        return new ODataStore({
            version: 4,
            url: `${this.odatabaseUrl}${this.controllerName}`,
            key: keyField,
        });
    }

    create(shopFact: RtShopFact): Observable<object> {
        const model = this.adapter.toServer(shopFact);

        return this.http
            .post(`${this.apibaseUrl}${this.secondControllerName}`, model)
            .pipe(catchError((err: HttpErrorResponse) => this.apiErrorHandler.handleError(err)));
    }

    update(shopNo: string, shopFact: RtShopFact): Observable<RtShopFact> {
        return this.http
            .put<RtShopFact>(
                `${this.apibaseUrl}${this.secondControllerName}/${shopNo}`,
                this.adapter.toServer(shopFact),
            )
            .pipe(
                map((data: RtShopFact) => {
                    return data ? this.adapter.adapt(data) : null;
                }),
                catchError((err: HttpErrorResponse) => this.apiErrorHandler.handleError(err)),
            );
    }

    delete(shopNo: string): Observable<void> {
        return this.http
            .delete<void>(`${this.apibaseUrl}${this.secondControllerName}/${shopNo}`)
            .pipe(
                map(() => {
                    return;
                }),
                catchError((err: HttpErrorResponse) => this.apiErrorHandler.handleError(err)),
            );
    }

    read(shopNo: string): Observable<RtShopFact> {
        return this.readViaOdata(shopNo);
    }

    readViaApi(shopNo: string): Observable<RtShopFact> {
        return this.http.get<RtShopFact>(`${this.apibaseUrl}${this.controllerName}/${shopNo}`).pipe(
            map((data: RtShopFact) => this.adapter.adapt(data)),
            catchError((error: HttpErrorResponse) => this.handleError(error)),
        );
    }

    readViaOdata(shopNo: string): Observable<RtShopFact> {
        return this.http
            .get<RtShopFact>(`${this.odatabaseUrl}${this.controllerName}('${shopNo}')`)
            .pipe(
                map((data: RtShopFact) => this.adapter.adapt(data)),
                catchError((error: HttpErrorResponse) => this.handleError(error)),
            );
    }

    handleError(error: HttpErrorResponse): Observable<never> {
        if (error.error instanceof ErrorEvent) {
            console.error('An error occurred:', error.error.message);
            return throwError('A browser or network error occurred');
        } else if (error.status === 404) {
            return throwError('Not Found');
        } else if (error.status === 422) {
            console.error('Found but cannot be used');
            return throwError(error.error);
        } else {
            console.error(`Backend returned code ${error.status}`, error);
            return throwError('The server returned an unknown error');
        }
    }
}
